import React, {useMemo, useState} from "react";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {makeRequired, makeValidate} from "mui-rff";
import Api from "../../../../../../../core/services/api/api";
import ApiResponseCodes from "../../../../../../../core/models/static/api-response-codes";
import {Col, Row} from "reactstrap";
import classnames from "classnames";
import {Fade} from "@mui/material";
import Form from "../../../../../base/form";
import Button from "../../../../../base/button/button";
import {MuiRFF, Yup} from '../../../../../form';
import ValidateMessages from "../../../../../../../core/models/static/texts/vallidate-messages";
import DateUtils from "../../../../../../../core/services/date-utils";
import ProductInformationPopover from "../../../../../app-specific/draggable-popover/product-information-popover";
import {EditPartSupportRequestProcessSteps} from "../../index";


const schema = Yup.object().shape({
    expiryDate: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .disablePastDate(ValidateMessages.minDate("today"), 'day')
        .minDate(() => DateUtils.serverToUTC(DateUtils.serverNow().startOf('day').add(1, 'day')), (params) => `Minimum date is ${DateUtils.UTCToLocal(params.min).format(DateUtils.dateFormat)}`)
        .nullable().required(ValidateMessages.required),
})


const EditPartSupportRequestReview = ({
                                          basicInfo,
                                          productInfo,
                                          request,
                                          setNewRequest,
                                          setProcess,
                                          terminateProcess,
                                          setProductInfo,
                                          warehouses,
                                      }) => {
    const [submitting, setSubmitting] = useState(false);
    const [showExpiration, setShowExpiration] = useState({in: false, date: null})
    const isMounted = useIsMounted();
    const validate = makeValidate(schema);
    const required = makeRequired(schema);

    const prevRecord = useMemo(() => ({
        rga: request?.rga ?? '--',
        type: request?.type ?? '--',
        company: request?.customer?.name ?? '--',
        warehouse: warehouses?.find(e => e.code === request?.warehouseCode)?.name ?? '--',
        partNo: request?.partNo ?? '--',
        invoiceNo: (!request?.invoiceNo?.trim()?.length ? null : request?.invoiceNo) ?? '--',
        serialNo: request?.serialNo ?? '--',
        qty: request?.unSeriliazablePartsCount ?? 0,
        isSerialized: !request?.unSeriliazablePartsCount
    }), [request, warehouses])

    const newRecord = useMemo(() => ({
        rga: basicInfo?.autoRga ? 'Auto' : basicInfo?.rga ?? '--',
        type: basicInfo?.type?.title ?? '--',
        company: basicInfo?.customer?.name ?? '--',
        warehouse: basicInfo?.warehouse?.name ?? '--',
        partNo: productInfo?.part?.partNumber ?? '--',
        invoiceNo: (!productInfo?.invoiceNo?.trim()?.length ? null : productInfo?.invoiceNo) ?? '--',
        serialNo: productInfo?.serialNumber ?? '--',
        qty: productInfo?.selectedQuantity ?? 0,
        isSerialized: !!productInfo?.isSerialized,
    }), [productInfo, basicInfo])

    const changes = useMemo(() => ({
        rga: prevRecord.rga !== newRecord.rga,
        type: prevRecord.type !== newRecord.type,
        company: prevRecord.company !== newRecord.company,
        warehouse: prevRecord.warehouse !== newRecord.warehouse,
        partNo: prevRecord.partNo !== newRecord.partNo,
        invoiceNo: prevRecord.invoiceNo !== newRecord.invoiceNo,
        serialNo: prevRecord.serialNo !== newRecord.serialNo,
        qty: prevRecord.qty !== newRecord.qty,
        isSerialized: prevRecord.isSerialized !== newRecord.isSerialized
    }), [prevRecord, newRecord])

    const noChanges = useMemo(() => !Object.values(changes).some(e => !!e), [changes]);

    /**
     * Updates the request with the edited values from the previous state or the newly given expiration date in the
     * server.
     *
     * * if the response of the api returns a duplicateRga error, navigates the operator back to basic info section
     * * if the response of th api returns an invalidSerialNumber error, navigates the operator back to product info
     * section
     * * if the response of th api returns an expiredPartSupport error, shows the expiry date input in this view.
     * @param {boolean} removeDocuments whether to remove the existing documents of the request upon editing the request
     * @param newExpDate
     */
    const updateRequest = (removeDocuments = false, newExpDate = null) => {
        setSubmitting(true);
        // TODO: implement remove documents when available:
        const forApi = {
            id: request.id,
            customerId: basicInfo.customer?.id,
            warehouseCode: basicInfo?.warehouse?.code,
            RGA: basicInfo.autoRga ? null : basicInfo?.rga,
            typeID: basicInfo.type?.id,
            serialNo: productInfo.isSerialized ? productInfo?.invoiceSerialNumber ?? productInfo?.serialNumber : null,
            unserializableItemsCount: productInfo.isSerialized ? null : productInfo.selectedQuantity,
            invoiceNo: productInfo.invoiceNo,
            partNo: productInfo.part?.partNumber,
            newStatusId: null,
            newExpDate: newExpDate ? DateUtils.UTCToServer(newExpDate).endOf('day').format(DateUtils.withoutOffsetFormat) : null,
        }
        Api.updatePartSupportRequest(forApi).then((response) => {
            if (!isMounted()) return;
            setSubmitting(false);
            if (!response?.resultFlag) {
                switch (response?.errorCode) {
                    case ApiResponseCodes.duplicateRGA:
                        return setProcess(EditPartSupportRequestProcessSteps.basicInfo);
                    case ApiResponseCodes.invalidSerialNumber:
                        return backToProductInfoSection();
                    case ApiResponseCodes.expiredPartSupport:
                        return setShowExpiration({
                            in: true,
                            date: (response?.message?.replace("The request is not" +
                                " applicable due to the exception expiry date :", ''))
                        });
                    default:
                        return;
                }
            }
            if (removeDocuments) {
                setNewRequest(response?.data?.request);
                setProcess(EditPartSupportRequestProcessSteps.documentation)
            } else {
                terminateProcess(response?.data?.request);
            }
        })
    }

    /**
     * Navigates the operator back to the product info section of the dialog.
     *
     * * if the operator used one of the invoice or warehouse entries to come to this view, navigates them back to the
     * table section of product info view
     * ( if the operator used the autocomplete to select their serial number, navigates them back to the first section
     * of product info view
     */
    const backToProductInfoSection = () => {
        if (productInfo.showProductOrSerialNoSelection) {
            // the operator used one of the entries of the selected part to come to this view, so we bring
            // them back to the invoices or warehouses section of product info
            setProductInfo(prevState => ({
                ...prevState,
                invoiceNo: null,
                warehouseCode: null,
                serialNumber: null,
                selectedQuantity: 0,
            }))
        } else {
            // the operator used the autocomplete to come to this view, so we bring them back to the
            // first section of product info
            setProductInfo(prevState => ({
                ...prevState,
                part: undefined,
                invoiceNo: null,
                warehouseCode: null,
                serialNumber: null,
                selectedQuantity: 0,
            }))
        }
        setProcess(EditPartSupportRequestProcessSteps.productInfo);
    }

    return (
        <>
            <div className={'d-flex flex-wrap w-100'}>
                <Col className={'flex-grow-1 pl-5'}>
                    <p className={'text-md font-weight-semi-bold mb-2'}>
                        Previous Record
                    </p>
                    <div className={'edit-psq-card'}>
                        <p className={classnames({'old': changes.rga})}>
                            RGA: <span>{prevRecord.rga}</span>
                        </p>
                        <p className={classnames({'old': changes.type})}>
                            Type: <span>{prevRecord.type}</span>
                        </p>
                        <p className={classnames({'old': changes.company})}>
                            Customer: <span>{prevRecord.company}</span>
                        </p>
                        <p className={classnames({'old': changes.warehouse})}>
                            Warehouse: <span>{prevRecord.warehouse}</span>
                        </p>
                        <p className={classnames('part-number-fluid', {'old': changes.partNo})}>
                            Part No: <ProductInformationPopover partNo={prevRecord.partNo} wrapperProps={{sx: {display: 'inline-block'}}}>
                            <span>{prevRecord.partNo}</span>
                        </ProductInformationPopover>
                        </p>
                        <p className={classnames({'old': changes.invoiceNo})}>
                            Invoice No: <span>{prevRecord.invoiceNo}</span>
                        </p>
                        {
                            prevRecord?.isSerialized
                                ? <p className={classnames({'old': changes.isSerialized || changes.serialNo})}>
                                    Serial No: <span>{prevRecord.serialNo}</span>
                                </p>
                                : <p className={classnames({'old': changes.isSerialized || changes.qty})}>
                                    Quantity: <span>{prevRecord.qty}</span>
                                </p>
                        }
                    </div>
                </Col>
                <Col className={'flex-grow-1 pr-5'}>
                    <p className={'text-md font-weight-semi-bold mb-2'}>
                        New Record
                    </p>
                    <div className={'edit-psq-card'}>
                        <p className={classnames({'new': changes.rga})}>
                            RGA: <span>{newRecord.rga}</span>
                        </p>
                        <p className={classnames({'new': changes.type})}>
                            Type: <span>{newRecord.type}</span>
                        </p>
                        <p className={classnames({'new': changes.company})}>
                            Customer: <span>{newRecord.company}</span>
                        </p>
                        <p className={classnames({'new': changes.warehouse})}>
                            Warehouse: <span>{newRecord.warehouse}</span>
                        </p>
                        <p className={classnames('part-number-fluid', {'new': changes.partNo})}>
                            Part No: <ProductInformationPopover partNo={newRecord.partNo} wrapperProps={{sx: {display: 'inline-block'}}}>
                            <span>{newRecord.partNo ?? ""}</span>
                        </ProductInformationPopover>
                        </p>
                        <p className={classnames({'new': changes.invoiceNo})}>
                            Invoice No: <span>{newRecord.invoiceNo}</span>
                        </p>
                        {
                            newRecord?.isSerialized
                                ? <p className={classnames({'new': changes.isSerialized || changes.serialNo})}>
                                    Serial No: <span>{newRecord.serialNo}</span>
                                </p>
                                : <p className={classnames({'new': changes.isSerialized || changes.qty})}>
                                    Quantity: <span>{newRecord.qty}</span>
                                </p>
                        }
                    </div>
                </Col>
                <div className={'w-100 my-3'}/>
                <Fade in={showExpiration.in} unmountOnExit mountOnEnter>
                    <div className={'d-flex flex-column m-auto align-items-center'}>
                        <Form
                            onSubmit={(values) => updateRequest(false, values.expiryDate)}
                            validate={validate}
                            id={'exp-form'}
                            className='w-100'
                            render={() => {
                                return (
                                    <>
                                        <p className={'edit-part-support-request-review-expiry'}>
                                            Warranty or Return has been expired
                                            on {DateUtils.serverToLocal(showExpiration.date).format(DateUtils.dateFormat)}
                                        </p>
                                        <MuiRFF.DatePicker
                                            name={'expiryDate'}
                                            label={"New Expiration Date"}
                                            required={required.expiryDate}
                                            disablePast
                                        />
                                    </>
                                )
                            }}
                        />
                    </div>
                </Fade>
                <div className={'w-100 my-2'}/>
                <Row className='no-gutters footer justify-content-between align-items-start w-100'>
                    <Button
                        variant={'dialog'}
                        color={'primary'}
                        disabled={submitting}
                        onClick={backToProductInfoSection}
                        borderRadius={'0 8px 0 8px'}
                    >
                        Back
                    </Button>
                    <Button
                        disabled={submitting}
                        variant={'dialog'}
                        color={'primary'}
                        {...(
                            showExpiration.in
                                ? {
                                    type: 'submit',
                                    form: 'exp-form'
                                }
                                : {
                                    onClick: () => noChanges
                                        ? terminateProcess()
                                        : updateRequest(false)
                                }
                        )}
                        borderRadius={'8px 0 8px 0'}
                    >
                        {
                            submitting
                                ? 'updating...'
                                : showExpiration.in
                                    ? 'Update Expiration'
                                    : noChanges
                                        ? "Done"
                                        : `Update Request`
                        }
                    </Button>
                </Row>
            </div>
        </>
    );
}


export default EditPartSupportRequestReview;
